import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { v4 as uuidv4 } from 'uuid';
import { OpportunityDetailFieldKey } from './hooks/useOpportunityDetailsForm';
import { fieldLabelMap } from './NewIdeation/constants';
import { uploadFile } from '../IdeationCOI/IdeationService';
import { setOpportunityDetails } from '../../slices/ideationSlice/newIdeationSlice';
import { getFileIcon, validateFile } from '../../common/utils';
import { RootState } from '../../store';
import { FileViewer } from '../NewSolutionXPLR/GeneralInformation/KeyInformation';
import CustomTooltip from '../../common/components/CustomToolTip/CustomToolTip';

interface IdeationIntake {
  id: string;
  type: string;
  use_case_id: string;
  conversation_id: string;
  created_by_name: string;
  version: string;
  function: string;
  process: string;
  ideation_name: string;
  level2_name: string;
}

interface ChatSession {
  id: string;
  use_case_title: string;
  created_by: string;
  created_at: string;
  usedInProcess: string;
  conversation_id: string;
  sessionName?: string;
  ideation_intake: IdeationIntake[];
}

// const FLOW_BUILDER_APP_ID_CURRENT = 'your-current-process-app-id';
// const FLOW_BUILDER_APP_ID_IMPACT = 'your-impact-app-id';
// const FLOW_BUILDER_APP_ID_IMPROVEMENT = 'your-improvement-app-id';
// const FLOW_BUILDER_APP_ID_KPI = 'your-kpi-app-id';

// const FIELD_APP_ID_MAP: Record<string, string> = {
//   current_process: FLOW_BUILDER_APP_ID_CURRENT,
//   expected_impact: FLOW_BUILDER_APP_ID_IMPACT,
//   ideas_of_improvement: FLOW_BUILDER_APP_ID_IMPROVEMENT,
//   specific_ideas_of_improvement: FLOW_BUILDER_APP_ID_IMPROVEMENT,
//   key_performance_indicator: FLOW_BUILDER_APP_ID_KPI,
// };

interface Process {
  id: string;
  name: string;
  date: string;
  subtitle?: string;
  context_knowledge?: Record<string, string>;
  use_case_id: string;
}

interface UploadedFile {
  id: string;
  name: string;
  file: File;
  url?: string;
  isUploading?: boolean;
  isAnalyzing?: boolean;
  fileData?: string;
  fileSize?: string;
  finalSummary?: string;
  extractedData?: any[];
  failed?: boolean;
  filePath?: string;
  error?: string;
}

// Draft file metadata - only serializable data (no File blobs)
interface DraftFileMetadata {
  id: string;
  name: string;
  url?: string;
  filePath?: string;
  fileSize?: string;
}

// Draft conversation structure for localStorage persistence
interface DraftConversation {
  draftId: string;
  details: string;
  fieldType: string;
  files: DraftFileMetadata[];
  createdAt: string;
  updatedAt: string;
}

// Storage key for draft conversation
const DRAFT_STORAGE_KEY = 'dataxplr_draft_conversation';

interface ProcessCreationState {
  details: string;
  uploadedFiles: UploadedFile[];
  selectedContexts: string[];
  recentProcesses: Process[];
  isLoadingRecents: boolean;
  contextProcesses: Process[];
  isLoadingContext: boolean;
  fileSummary: string;
  attachedContexts: Process[];
}

interface ProcessCreationProps {
  // handleBack: () => void;
  field: string;
  handleForward: (id: string) => void;
  sessions?: ChatSession[];
  currentChatId?: string;
  onSelectChat?: (chatId: string) => void;
  onNewChat?: () => void;
  onNewIdeation?: () => void;
  setShowKnowledgeCreation?: (flag: boolean) => void;
  onFieldChange?: (field: string) => void;
}

const ProcessCreation: React.FC<ProcessCreationProps> = ({
  // handleBack,
  field,
  handleForward,
  currentChatId: propCurrentChatId,
  setShowKnowledgeCreation = () => {},
  onFieldChange,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filePreview, setFilePreview] = useState<any>(null);
  const organization_id = localStorage.getItem('current_organization') || '';

  const [currentChatId, setCurrentChatId] = useState<string>('');

  // Navigation state - similar to KnowledgeCreation
  const [_useCaseId, setUseCaseId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'error',
  });

  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const mapFieldKeyToApi = (f: OpportunityDetailFieldKey | string) => {
    if (!f) return 'current_process';
    const key = String(f);

    switch (key) {
      case 'currentProcess':
      case 'current_process':
        return 'current_process';
      case 'expectedImpact':
      case 'expected_impact':
        return 'expected_impact';
      case 'specificIdeasOfImprovement':
      case 'specific_ideas_of_improvement':
      case 'ideas_of_improvement':
        return 'ideas_of_improvement';
      case 'keyPerformanceIndicators':
      case 'key_performance_indicator':
        return 'key_performance_indicator';
      default:
        return 'current_process';
    }
  };

  const [selectValue, setSelectValue] = useState<string>(() => mapFieldKeyToApi(field));

  const fieldTypeOptions = [
    { value: 'current_process', label: 'Current Process' },
    { value: 'expected_impact', label: 'Expected Impact' },
    { value: 'ideas_of_improvement', label: 'Specific Idea(s) for Improvement' },
    { value: 'key_performance_indicator', label: 'Key Performance Indicators' },
  ];

  const [state, setState] = useState<ProcessCreationState>({
    details: '',
    uploadedFiles: [],
    selectedContexts: [],
    recentProcesses: [],
    isLoadingRecents: true,
    contextProcesses: [],
    isLoadingContext: false,
    fileSummary: '',
    attachedContexts: [],
  });

  // Draft ID ref - generated on first user action
  const draftIdRef = useRef<string>('');

  const detailsRef = useRef(state.details);
  const uploadedFilesRef = useRef(state.uploadedFiles);
  const prevFieldRef = useRef<string>(selectValue);

  // Draft utility functions
  const saveDraftToStorage = (draft: Partial<DraftConversation>) => {
    try {
      const existingDraft = loadDraftFromStorage();
      const now = new Date().toISOString();

      // Generate draft ID if not exists
      if (!draftIdRef.current) {
        draftIdRef.current = existingDraft?.draftId || uuidv4();
      }

      // Only save files that have completed upload (have url or filePath)
      const filesToSave: DraftFileMetadata[] = (draft.files || existingDraft?.files || [])
        .filter((f: DraftFileMetadata) => f.url || f.filePath)
        .map((f: DraftFileMetadata) => ({
          id: f.id,
          name: f.name,
          url: f.url,
          filePath: f.filePath,
          fileSize: f.fileSize,
        }));

      const draftToSave: DraftConversation = {
        draftId: draftIdRef.current,
        details: draft.details ?? existingDraft?.details ?? '',
        fieldType: draft.fieldType ?? existingDraft?.fieldType ?? selectValue,
        files: filesToSave,
        createdAt: existingDraft?.createdAt || now,
        updatedAt: now,
      };

      // Only save if there's content
      if (draftToSave.details.trim() || draftToSave.files.length > 0) {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftToSave));
      } else {
        clearDraftFromStorage();
      }
    } catch (e) {
      console.error('Failed to save draft:', e);
    }
  };

  const loadDraftFromStorage = (): DraftConversation | null => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved) as DraftConversation;
      }
    } catch (e) {
      console.error('Failed to load draft:', e);
    }
    return null;
  };

  const clearDraftFromStorage = () => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      draftIdRef.current = '';
    } catch (e) {
      console.error('Failed to clear draft:', e);
    }
  };

  // Keep refs in sync
  useEffect(() => {
    detailsRef.current = state.details;
  }, [state.details]);

  useEffect(() => {
    uploadedFilesRef.current = state.uploadedFiles;
  }, [state.uploadedFiles]);

  useEffect(() => {
    const newField = mapFieldKeyToApi(field);
    if (newField !== selectValue) {
      setSelectValue(newField);
    }
  }, [field]);

  // Load draft on mount (only for new sessions, not existing chats)
  useEffect(() => {
    if (propCurrentChatId) return; // do not load drafts when editing existing chat

    const draft = loadDraftFromStorage();
    if (draft) {
      draftIdRef.current = draft.draftId;

      // Restore text
      setState((prev) => ({ ...prev, details: draft.details }));

      // Restore file metadata - create placeholder UploadedFile objects
      if (draft.files && draft.files.length > 0) {
        const restoredFiles: UploadedFile[] = draft.files.map((f) => ({
          id: f.id,
          name: f.name,
          file: new File([], f.name), // Placeholder - actual file not available
          url: f.url,
          filePath: f.filePath,
          fileSize: f.fileSize,
          isUploading: false,
          isAnalyzing: false,
        }));
        setState((prev) => ({ ...prev, uploadedFiles: restoredFiles }));
      }

      // Restore field type if different
      if (draft.fieldType && draft.fieldType !== selectValue) {
        setSelectValue(draft.fieldType);
        if (onFieldChange) {
          onFieldChange(draft.fieldType);
        }
      }
    } else {
      setState((prev) => ({ ...prev, details: '', uploadedFiles: [] }));
    }

    prevFieldRef.current = selectValue;
  }, [propCurrentChatId]);

  // Save draft when text or files change (debounced effect)
  useEffect(() => {
    if (propCurrentChatId) return; // don't persist drafts for existing chats

    // Convert current uploadedFiles to DraftFileMetadata
    const fileMetadata: DraftFileMetadata[] = state.uploadedFiles
      .filter((f) => !f.isUploading && !f.failed && (f.url || f.filePath))
      .map((f) => ({
        id: f.id,
        name: f.name,
        url: f.url,
        filePath: f.filePath,
        fileSize: f.fileSize,
      }));

    saveDraftToStorage({
      details: state.details,
      fieldType: selectValue,
      files: fileMetadata,
    });
  }, [state.details, state.uploadedFiles, selectValue, propCurrentChatId]);

  // Sync propCurrentChatId
  useEffect(() => {
    if (propCurrentChatId && propCurrentChatId !== currentChatId) {
      setCurrentChatId(propCurrentChatId);
    }
  }, [propCurrentChatId]);

  const { currentProcess, expectedImpact, specificIdeasOfImprovement, id } = useSelector(
    (state: RootState) => state.newIdeation
  );

  useEffect(() => {
    const shouldSendContext = sessionStorage.getItem('sendContextForField');
    const contextFieldValue = sessionStorage.getItem('contextFieldValue');

    if (shouldSendContext && field === shouldSendContext && contextFieldValue) {
      const plainText = stripHtmlTags(contextFieldValue);

      setState((prev) => ({
        ...prev,
        details: plainText,
      }));

      sessionStorage.removeItem('sendContextForField');
      sessionStorage.removeItem('contextFieldValue');
    }
  }, [field]);

  const showSnackbar = (message: string, severity: 'error' | 'success' | 'warning' = 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const filesArray = Array.from(files);
      const validatedFiles: { file: File; index: number }[] = [];

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        const validation = await validateFile(file);

        if (!validation.ok) {
          setState((prev) => ({
            ...prev,
            uploadedFiles: [
              ...prev.uploadedFiles,
              {
                id: `failed-${Date.now()}-${i}`,
                name: file.name,
                file,
                isUploading: false,
                isAnalyzing: false,
                failed: true,
                error: validation?.error,
              },
            ],
          }));

          continue;
        } else {
          validatedFiles.push({ file, index: i });
        }
      }

      if (validatedFiles.length === 0) {
        if (event.target) {
          event.target.value = '';
        }
        return;
      }

      const tempFiles: UploadedFile[] = validatedFiles.map(({ file, index }) => ({
        id: `file-${Date.now()}-${index}`,
        name: file.name,
        file,
        isUploading: true,
      }));

      setState((prev) => ({
        ...prev,
        uploadedFiles: [...prev.uploadedFiles, ...tempFiles],
      }));

      for (let i = 0; i < tempFiles.length; i++) {
        const tempFile = tempFiles[i];
        try {
          const response = await uploadFile(tempFile.file);

          if (response && response.files && response.files.length > 0) {
            const fileUrl = response.files[0];

            setState((prev) => ({
              ...prev,
              uploadedFiles: prev.uploadedFiles.map((f) =>
                f.id === tempFile.id
                  ? {
                      ...f,
                      url: fileUrl,
                      isUploading: false,
                      isAnalyzing: false,
                      filePath: response.files[0],
                      fileSize: `${Math.round(tempFile.file.size / 1024)}KB`,
                    }
                  : f
              ),
            }));
          } else {
            setState((prev) => ({
              ...prev,
              uploadedFiles: prev.uploadedFiles.map((f) =>
                f.id === tempFile.id
                  ? {
                      ...f,
                      isUploading: false,
                      isAnalyzing: false,
                      failed: true,
                    }
                  : f
              ),
            }));

            showSnackbar(`Upload failed for ${tempFile.name}`, 'error');
            console.error('File upload failed for:', tempFile.name);
          }
        } catch (error) {
          setState((prev) => ({
            ...prev,
            uploadedFiles: prev.uploadedFiles.map((f) =>
              f.id === tempFile.id
                ? {
                    ...f,
                    isUploading: false,
                    isAnalyzing: false,
                    failed: true,
                  }
                : f
            ),
          }));

          showSnackbar(`Upload failed for ${tempFile.name}`, 'error');
          console.error('Error uploading file:', tempFile.name, error);
        }
      }
    }

    if (event.target) {
      event.target.value = '';
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setState((prev) => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter((f) => f.id !== fileId),
    }));
  };

  // const handleRemoveContext = (contextId: string) => {
  //   setState((prev) => ({
  //     ...prev,
  //     selectedContexts: prev.selectedContexts.filter((c) => c !== contextId),
  //   }));
  // };

  // const handleRecentProcessClick = (process: Process) => {
  //   handleForward(process?.use_case_id);
  // };

  // const handleContextToggle = (processId: string) => {
  //   setState((prev) => ({
  //     ...prev,
  //     selectedContexts: prev.selectedContexts.includes(processId)
  //       ? prev.selectedContexts.filter((c) => c !== processId)
  //       : [...prev.selectedContexts, processId],
  //   }));
  // };

  const handleDetailsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      details: event.target.value,
    }));
  };

  const isForwardEnabled = () => {
    const { details, uploadedFiles = [] } = state;

    if (details.trim() || uploadedFiles.length) {
      if (uploadedFiles.some((f) => f.isUploading || f.isAnalyzing || f.failed)) {
        return false;
      }
      return true;
    }

    return false;
  };

  // const handleSidebarFieldTypeChange = (value: string) => {
  //   setSidebarSelectValue(value);
  // };

  // const getProcessNameById = (processId: string): string => {
  //   const process = state.contextProcesses.find((p) => p.id === processId);
  //   return process ? process.name : processId;
  // };

  const handleSubmit = async (_useCaseId: string = currentChatId || '') => {
    if (!isForwardEnabled() || isSubmitting) {
      return;
    }

    // Add validation check
    if (!organization_id) {
      showSnackbar('Organization ID is missing. Please login again.', 'error');
      console.error('Organization ID is empty');
      return;
    }

    setIsSubmitting(true);

    try {
      // Generate UUID
      const generatedUuid = uuidv4();

      handleForward(generatedUuid);

      showSnackbar('Query submitted successfully!', 'success');

      // Dispatch to Redux
      dispatch(
        setOpportunityDetails({
          uploadedFiles: state.uploadedFiles,
          // selectedContexts: [...selectedContextsArr, ...attachedContextsPayload],
          detailsText: state.details,
          // taxonomy: taxonomyData,
          finalSummary: state.fileSummary,
        })
      );

      // Clear related draft(s) from localStorage
      try {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      } catch (e) {
        console.error('Failed to clear draft from localStorage', e);
      }

      // Set states
      setUseCaseId(generatedUuid);
      setShowKnowledgeCreation(false);
      clearDraftFromStorage();

      // Note: The API call will be triggered by DataXPLR component when it detects
      // the route with UUID and no existing chat messages
    } catch (error) {
      console.error('Error submitting query:', error);
      showSnackbar('An error occurred. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const reduxMap = {
      currentProcess: currentProcess,
      expectedImpact: expectedImpact,
      specificIdeasOfImprovement: specificIdeasOfImprovement,
    };

    const newAttached: Process[] = [];

    Object.entries(reduxMap).forEach(([key, value]) => {
      if (value && key !== field) {
        newAttached.push({
          id: key,
          name: fieldLabelMap[key] ?? key,
          date: '',
          use_case_id: id,
          context_knowledge: { [key]: value },
        });
      }
    });

    setState((prev) => ({
      ...prev,
      attachedContexts: newAttached,
    }));
  }, [currentProcess, expectedImpact, specificIdeasOfImprovement, field, id]);

  const handlePaste = async (event: React.ClipboardEvent) => {
    if (!event.clipboardData) return;

    const items = event.clipboardData.items;
    if (!items) return;

    const imageItem = Array.from(items).find((item) => item.type.startsWith('image/'));

    if (imageItem) {
      event.preventDefault();

      const file = imageItem.getAsFile();
      if (!file) return;

      const validation = await validateFile(file);
      if (!validation.ok) {
        showSnackbar(validation?.error || 'File validation failed', 'error');
        return;
      }

      const tempFile: UploadedFile = {
        id: `pasted-${Date.now()}`,
        name: file.name || 'pasted-image.png',
        file,
        isUploading: true,
      };

      setState((prev) => ({
        ...prev,
        uploadedFiles: [...prev.uploadedFiles, tempFile],
      }));

      try {
        const response = await uploadFile(file);

        if ((response?.files || [])?.length > 0) {
          const fileUrl = response?.files?.[0];

          setState((prev) => ({
            ...prev,
            uploadedFiles: prev.uploadedFiles.map((f: UploadedFile) =>
              f.id === tempFile.id
                ? {
                    ...f,
                    url: fileUrl,
                    isUploading: false,
                    isAnalyzing: false,
                    filePath: response?.files[0],
                    fileSize: `${Math.round(file.size / 1024)}KB`,
                  }
                : f
            ),
          }));
        } else {
          setState((prev) => ({
            ...prev,
            uploadedFiles: prev.uploadedFiles.map((f: UploadedFile) =>
              f.id === tempFile.id
                ? {
                    ...f,
                    isUploading: false,
                    isAnalyzing: false,
                    failed: true,
                  }
                : f
            ),
          }));
          showSnackbar('Image paste upload failed', 'error');
        }
      } catch (error) {
        showSnackbar('Image paste upload failed', 'error');

        setState((prev) => ({
          ...prev,
          uploadedFiles: prev.uploadedFiles.filter((f: UploadedFile) => f.id !== tempFile.id),
        }));
      }

      return;
    }

    const pastedText = event.clipboardData.getData('text');

    if (pastedText) {
      event.preventDefault();

      const input = event.target as HTMLTextAreaElement;
      const start = input.selectionStart;
      const end = input.selectionEnd;

      const newValue = state.details.slice(0, start) + pastedText + state.details.slice(end);

      setState((prev) => ({
        ...prev,
        details: newValue,
      }));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        backgroundColor: '#ffffff',
        width: '100%',
        justifyContent: 'center',
      }}
    >
      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              cursor: 'pointer',
            }}
          >
            {/* <IconButton
              size="small"
              sx={{
                color: 'primary.main',
                backgroundColor: 'white',
                border: '1px solid #DBDBDB',
                borderRadius: '50%',
              }}
              onClick={handleBack}
            >
              <ArrowBackIcon sx={{ height: 18, width: 18 }} />
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontSize: '14px',
              }}
            >
              {`New Ideation/${fieldLabelMap[field]}`}
            </Typography> */}
          </Box>
        </Box>

        {/* Main Content - Centered */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            // maxWidth: 1000,
            // mx: 'auto',
            // px: { xs: 2, sm: 4, md: 8, lg: 12, xl: 20 },
            // py: { xs: 4, sm: 6, md: 8, lg: 10 },
            width: '100%',
            px: 2,
          }}
        >
          <Box
            sx={{
              width: '85%',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: '128px',
            }}
          >
            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                color: '#1a1a1a',
                fontSize: '28px',
              }}
            >
              Create Knowledge Using Data XPLR
            </Typography>

            {/* Subtitle */}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontFamily: 'Poppins',
                color: '#131426',
                mb: 4,
                fontSize: '15px',
              }}
            >
              Add instructions or files to generate semantic knowledge for selected category
            </Typography>

            {/* Input Section */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '1078px',
                  minHeight: '124px',
                  border: '1px solid rgba(115, 115, 115, 1)',
                  borderRadius: '10px',
                  padding: { xs: '10px', sm: '12px' },
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  '&:focus-within': {
                    borderColor: '#2563eb',
                  },
                }}
              >
                {/* File attachments display */}
                {state.uploadedFiles.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {state.uploadedFiles.map((file) => (
                        <Box
                          key={file.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'start',
                            border: file.failed ? '1px solid #D32F2F' : '1px solid #E0E0E0',
                            // backgroundColor: file.failed ? '#FFECEC' : '#fafafa',
                            borderRadius: '4px',
                            py: 0.5,
                            px: 1,
                            minWidth: 180,
                            maxWidth: '100%',
                            opacity: file.isUploading || file.isAnalyzing ? 0.6 : 1,
                            cursor: 'pointer',
                          }}
                          onClick={() => setFilePreview(file.url)}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '6px',
                              bgcolor: '#f1f3f4',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              mr: 1,
                            }}
                          >
                            {file.isUploading || file.isAnalyzing ? (
                              <CircularProgress size={14} sx={{ color: '#5f6368' }} />
                            ) : file.failed ? (
                              <CustomTooltip
                                title={file?.error || ''}
                                variant="bottom"
                                offset={[0, 0]}
                              >
                                <img
                                  src="/images/Error.svg"
                                  alt="error"
                                  style={{ width: 16, height: 16 }}
                                />
                              </CustomTooltip>
                            ) : (
                              getFileIcon(file.name, file.file)
                            )}
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              flex: 1,
                              fontWeight: 500,
                              fontSize: '14px',
                              color: '#202124',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mt: '4px',
                              alignItems: 'start',
                            }}
                          >
                            {file.name}
                          </Typography>

                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(file.id);
                            }}
                            disabled={file.isUploading || file.isAnalyzing || isSubmitting}
                            sx={{
                              ml: 0.5,
                              color: '#5f6368',
                              '&:hover': { bgcolor: 'transparent', color: '#000' },
                              '&:disabled': { color: '#ccc' },
                            }}
                          >
                            <CloseIcon sx={{ height: 20, width: 20 }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    placeholder="Add instructions to generate knowledge"
                    value={state.details}
                    onChange={handleDetailsChange}
                    onPaste={handlePaste}
                    variant="standard"
                    disabled={isSubmitting}
                    sx={{
                      '& .MuiInputBase-input::placeholder': {
                        color: '#737373',
                        opacity: 1,
                      },
                    }}
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        fontSize: '16px',
                        color: '#000000',
                        '& textarea': {
                          minHeight: '24px',
                          maxHeight: '200px',
                          overflowY: 'auto !important',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Bottom section with field type selector and add file button */}
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                      gap: 1,
                      mt: 1.5,
                      pt: 1.5,
                    }}
                  >
                    <FormControl size="small">
                      <Select
                        value={selectValue}
                        onChange={(e) => {
                          const newValue = e.target.value as string;

                          // Save current draft with new field type
                          if (!propCurrentChatId) {
                            saveDraftToStorage({ fieldType: newValue });
                          }

                          setSelectValue(newValue);
                          prevFieldRef.current = newValue;
                          if (onFieldChange) {
                            onFieldChange(newValue);
                          }
                        }}
                        displayEmpty
                        disabled={isSubmitting}
                        MenuProps={{
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            sx: {
                              width: 266,
                              minWidth: 266,
                              boxShadow: 'none',
                              border: '1px solid #B9B9B9',
                              borderRadius: '6px',
                              '& .MuiMenuItem-root': {
                                width: 266,
                                minWidth: 266,
                                boxSizing: 'border-box',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                paddingLeft: '5px',
                                paddingRight: '10px',
                              },
                            },
                          },
                        }}
                        sx={{
                          fontFamily: 'Poppins',
                          fontWeight: 500,
                          fontStyle: 'normal',
                          fontSize: '16px',
                          lineHeight: '100%',
                          letterSpacing: '-0.02em',
                          width: '166px',
                          height: '40px',
                          opacity: 1,
                          borderRadius: '6px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(185, 185, 185, 1)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d0d0d0',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#898989',
                          },
                          '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '11px',
                          },
                        }}
                      >
                        {fieldTypeOptions.map((option) => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                              fontFamily: 'Poppins',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '100%',
                              letterSpacing: '-0.02em',
                              width: '266px',
                              minWidth: '266px',
                              boxSizing: 'border-box',
                              py: 1.5,
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              px: 1.25,
                              mx: 0.4,
                            }}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      startIcon={
                        <AttachFileIcon sx={{ fontSize: 18, transform: 'rotate(45deg)' }} />
                      }
                      variant="outlined"
                      size="small"
                      onClick={handleAddFile}
                      disabled={isSubmitting}
                      sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '16px',
                        lineHeight: '100%',
                        letterSpacing: '-0.02em',
                        color: '#131426',
                        borderColor: 'rgba(185, 185, 185, 1)',
                        textTransform: 'none',
                        borderRadius: '6px',
                        width: '105.34px',
                        height: '40px',
                        opacity: 1,
                        gap: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          borderColor: '#d0d0d0',
                        },
                        '& .MuiButton-startIcon': {
                          margin: 0,
                        },
                      }}
                    >
                      Add file
                    </Button>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleSubmit()}
                    disabled={!isForwardEnabled() || isSubmitting}
                    sx={{
                      backgroundColor: '#2563eb',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '13px',
                      borderRadius: '6px',
                      minWidth: 'auto',
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      '&:hover': {
                        backgroundColor: '#1d4ed8',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(163, 187, 253, 1)',
                        color: 'white',
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={18} sx={{ color: '#999' }} />
                    ) : (
                      <img src="/images/send-icon.png" alt="send" width={18} height={18} />
                    )}
                  </Button>
                </Box>

                <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileUpload} />
              </Box>

              {state.uploadedFiles.some((f) => f.failed) && (
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    width: '100%',
                    maxWidth: '1078px',
                  }}
                >
                  {state.uploadedFiles
                    .filter((f) => f.failed)
                    .map((failedFile) => (
                      <Box
                        key={failedFile.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: '#FFECEC',
                          border: '1px solid #FF6142',
                          borderRadius: '6px',
                          padding: '8px 12px',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: '#FF6142',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                lineHeight: 1,
                              }}
                            >
                              !
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              fontSize: '14px',
                              color: '#000',
                              fontWeight: 500,
                            }}
                          >
                            {failedFile.name}:{' '}
                            {failedFile.error || 'Unsupported file format or size '}
                          </Typography>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() => {
                            setState((prev) => ({
                              ...prev,
                              uploadedFiles: prev.uploadedFiles.filter(
                                (f) => f.id !== failedFile.id
                              ),
                            }));
                          }}
                          sx={{
                            color: '#000',
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.05)',
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* File Viewer Modal */}
      {filePreview && (
        <FileViewer
          fileUrl={filePreview}
          onClose={() => setFilePreview(null)}
          fileName={state.uploadedFiles.find((f) => f.url === filePreview)?.name || ''}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcessCreation;
